import { useCallback } from "react";
import {
  createUserDisplaySetting,
  updateUserDisplaySetting,
} from "@/api/user-display-settings-api";
import {
  DEFAULT_COLUMNS,
  MEASUREMENT_COLUMNS,
} from "@/constants/operating-points-constants";
import { useUnsavedOperatingPointChanges } from "@/hooks/useUnsavedOperatingPointChanges";
import { getOperatingPointsSettingsNameFromTabLabel } from "@/lib/operating-points-tab-utils.js";

/**
 * Build columns payload for a tab (shared by handleSave and handleSaveAll).
 * @param {string} tabId
 * @param {Object.<string, Array>} customColumns
 * @param {Function} mergeMeasurementColumns
 * @returns {{ jsonString: string, measurementCols: Array }}
 */
function buildColumnsPayloadForTab(
  tabId,
  customColumns,
  mergeMeasurementColumns
) {
  const defaultMeasurementCols =
    tabId && tabId.startsWith("custom-")
      ? []
      : tabId
        ? MEASUREMENT_COLUMNS[tabId] || []
        : [];
  const customCols = customColumns[tabId] || [];
  const measurementCols = mergeMeasurementColumns(
    tabId,
    defaultMeasurementCols,
    customCols
  );
  const allColumns = [...DEFAULT_COLUMNS, ...measurementCols];
  const columnsForApi = allColumns.map((col) => ({
    labelName: col.labelName,
    tagName: col.tagName ?? "",
    unit: col.unit || "",
    ...(col.universalDescriptorId != null
      && Number.isFinite(Number(col.universalDescriptorId))
      ? { universalDescriptorId: Number(col.universalDescriptorId) }
      : {}),
  }));
  const jsonString = JSON.stringify(columnsForApi);
  return { jsonString, measurementCols };
}

/**
 * Custom hook for save/discard handlers and unsaved changes detection
 * @param {Object} params - Handler dependencies
 * @returns {Object} Save handlers and unsaved changes state
 */
export function useOperatingPointsSaveHandlers({
  tabs,
  activeTab,
  customColumns,
  setCustomColumns,
  savedColumns,
  setReplacedDefaultTabs,
  viewNameToBackendId,
  setIsSaving,
  mergeMeasurementColumns,
  applyOperatingPointsViewsFromApiResponse,
}) {
  const {
    checkTabHasUnsavedChanges,
    hasUnsavedChanges,
    getUnsavedTabs,
    unsavedViewNames,
    hasAnyUnsavedChanges,
  } = useUnsavedOperatingPointChanges({
    tabs,
    activeTab,
    customColumns,
    savedColumns,
  });

  /** Save a single tab (update existing custom view or create from default). Used by handleSave and handleSaveAll. */
  const saveSingleTab = useCallback(
    async (tab) => {
      const tabId = tab.id;
      const { jsonString } = buildColumnsPayloadForTab(
        tabId,
        customColumns,
        mergeMeasurementColumns
      );
      const settingsName = getOperatingPointsSettingsNameFromTabLabel(tab.label);
      const requestBody = {
        PageName: "OperatingPoints",
        UserDisplaySettingsName: settingsName,
        UserDisplaySettingsData: jsonString,
      };
      const isActiveTab = tabId === activeTab;

      if (tabId.startsWith("custom-")) {
        const userDisplaySettingsId = viewNameToBackendId[tabId];
        if (
          !userDisplaySettingsId
          || typeof userDisplaySettingsId !== "number"
        ) {
          return;
        }
        const updateResponse = await updateUserDisplaySetting(
          userDisplaySettingsId,
          requestBody
        );
        applyOperatingPointsViewsFromApiResponse(updateResponse, {
          activateTabId: isActiveTab ? tabId : undefined,
        });
      } else {
        const response = await createUserDisplaySetting(requestBody);
        const applied = applyOperatingPointsViewsFromApiResponse(response, {
          activateTabLabel: settingsName,
        });
        if (applied) {
          setReplacedDefaultTabs((prev) => ({
            ...prev,
            [tabId]: settingsName,
          }));
        }
      }
    },
    [
      activeTab,
      customColumns,
      viewNameToBackendId,
      mergeMeasurementColumns,
      applyOperatingPointsViewsFromApiResponse,
      setReplacedDefaultTabs,
    ]
  );

  const handleSave = useCallback(async () => {
    if (!activeTab) return;
    const currentTab = tabs.find((tab) => tab.id === activeTab);
    if (!currentTab) return;
    try {
      setIsSaving(true);
      await saveSingleTab(currentTab);
    } catch (error) {
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [activeTab, tabs, setIsSaving, saveSingleTab]);

  const handleSaveAll = useCallback(async () => {
    const unsavedTabs = getUnsavedTabs();
    if (unsavedTabs.length === 0) return;
    try {
      setIsSaving(true);
      for (const tab of unsavedTabs) {
        await saveSingleTab(tab);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [getUnsavedTabs, setIsSaving, saveSingleTab]);

  const handleDiscardAll = useCallback(() => {
    const unsavedTabs = getUnsavedTabs();
    if (unsavedTabs.length === 0) return;

    setCustomColumns((prev) => {
      const updated = { ...prev };
      unsavedTabs.forEach((tab) => {
        const savedCols = savedColumns[tab.id] || [];
        if (!tab.id.startsWith("custom-")) {
          updated[tab.id] = [];
        } else {
          updated[tab.id] = JSON.parse(JSON.stringify(savedCols));
        }
      });
      return updated;
    });
  }, [getUnsavedTabs, savedColumns, setCustomColumns]);

  return {
    handleSave,
    handleSaveAll,
    handleDiscardAll,
    checkTabHasUnsavedChanges,
    hasUnsavedChanges,
    getUnsavedTabs,
    unsavedViewNames,
    hasAnyUnsavedChanges,
  };
}
